import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2 } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import PreviaCardapio from "./PreviaCardapio";
import { enviarPlanilha, mensagemDeErro } from "./api";

const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

function textoDoBotao(mudar, novos, faltam) {
  if (faltam) return `Escolha a categoria de ${plural(faltam, "produto novo", "produtos novos")}`;
  if (!mudar && !novos) return "Nada para aplicar";
  const partes = [];
  if (mudar) partes.push(plural(mudar, "produto alterado", "produtos alterados"));
  if (novos) partes.push(plural(novos, "produto novo", "produtos novos"));
  return `Aplicar: ${partes.join(" e ")}`;
}

/**
 * Confirmação da importação. O "Aplicar" reenvia o MESMO arquivo com
 * confirmar=true — o servidor refaz a análise em vez de confiar no que a tela
 * mostrou (o cardápio pode ter mudado entre a prévia e o clique) e grava tudo
 * numa transação só.
 */
export default function ImportarCardapioDialog({ arquivo, previa, onFechar, onImportado }) {
  const [gravando, setGravando] = useState(false);
  // Categoria escolhida por linha para produto novo sem categoria na planilha.
  const [escolhas, setEscolhas] = useState({});
  useEffect(() => { setEscolhas({}); }, [previa]);
  const aberto = !!(arquivo && previa);
  const mudar = previa?.resumo?.atualizar || 0;
  const novos = previa?.resumo?.novos || 0;
  const semCategoria = (previa?.novos || []).filter((n) => n.sem_categoria);
  const faltam = semCategoria.filter((n) => !escolhas[n.linha]).length;
  const escolher = (linha, categoria) => setEscolhas((e) => ({ ...e, [linha]: categoria }));
  const escolherTodos = (categoria) =>
    setEscolhas(Object.fromEntries(semCategoria.map((n) => [n.linha, categoria])));

  const aplicar = async () => {
    setGravando(true);
    try {
      const res = await enviarPlanilha(arquivo, true, escolhas);
      const feito = [];
      if (res.atualizados) feito.push(plural(res.atualizados, "produto atualizado", "produtos atualizados"));
      if (res.criados?.length) feito.push(plural(res.criados.length, "produto criado", "produtos criados"));
      toast.success(feito.length ? `${feito.join(" e ")} pela planilha` : "Nada mudou");
      onImportado?.();
      onFechar();
    } catch (e) {
      toast.error(await mensagemDeErro(e, "Não foi possível importar a planilha"));
    } finally {
      setGravando(false);
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={(v) => { if (!v && !gravando) onFechar(); }}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl rounded-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Importar planilha de produtos
          </DialogTitle>
          <p className="text-xs text-muted-foreground break-all">{arquivo?.name}</p>
        </DialogHeader>

        {previa && (
          <PreviaCardapio previa={previa} escolhas={escolhas} onEscolher={escolher} onEscolherTodos={escolherTodos} />
        )}

        <p className="text-[11px] text-muted-foreground">
          Só Preço, Descrição, Unidade de venda, Vende fracionado e os campos fiscais são gravados — o resto da
          planilha é consulta. Célula vazia não apaga nada; APAGAR limpa a descrição ou um campo fiscal.
        </p>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onFechar}
            disabled={gravando}
            className="px-4 py-2 rounded-lg border text-sm hover:bg-muted/50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={aplicar}
            disabled={gravando || faltam > 0 || (!mudar && !novos)}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50"
          >
            {gravando && <Loader2 className="w-4 h-4 animate-spin" />}
            {textoDoBotao(mudar, novos, faltam)}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
